import type { PauseOrResumeOrderActionProps } from './PauseOrResumeOrderAction.types';
import { SPLIT_ORDER_TEXT } from './PauseOrResumeOrderAction.types';
import { PauseOrderActionContent } from './PauseOrderActionContent';
import { useCreateOrderHold } from './hooks/useCreateOrderHold';
import { FlossPalette, Icon, RootActionDialog, styled } from '@orthly/ui';
import { Box, Button, Text } from '@orthly/ui-primitives';
import React from 'react';

const SplitOrderContentBox = styled(Box)({
    background: FlossPalette.SECONDARY_BACKGROUND,
    width: '100%',
    position: 'absolute',
    left: 0,
    bottom: 0,
    padding: '8px 16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'start',
    gap: '8px',
});

const SplitOrderIcon = styled(Icon)({
    color: FlossPalette.SECONDARY_FOREGROUND,
    fontSize: 12,
});

const SplitOrderBanner: React.VFC = () => {
    return (
        <SplitOrderContentBox data-testid={'split-order-banner'}>
            <SplitOrderIcon icon={'ExclamationIcon'} color={'inherit'} />
            <Text variant={'body2'} medium color={'SECONDARY_FOREGROUND'}>
                {SPLIT_ORDER_TEXT}
            </Text>
        </SplitOrderContentBox>
    );
};

type PauseOrderActionProps = PauseOrResumeOrderActionProps;

export const PauseOrderAction: React.VFC<PauseOrderActionProps> = ({
    order,
    refetch,
    leftAlign,
    buttonVariant,
    preselectedOption,
}) => {
    const [open, setOpen] = React.useState(false);
    const [selectedOption, setSelectedOption] = React.useState<string | undefined>(preselectedOption);

    const orderHasSplitOrders = (order.split_order_count ?? 0) > 0;

    const openPauseOrderModal = React.useCallback(() => {
        // TODO: [EPDPOR-2150] implement analytics
        setOpen(true);
    }, [setOpen]);

    const closePauseOrderModal = React.useCallback(() => {
        // TODO: [EPDPOR-2150] implement analytics
        setOpen(false);
        setSelectedOption(undefined);
    }, [setOpen, setSelectedOption]);

    const { submit: submitCreateHold, submitting: submittingCreateHold } = useCreateOrderHold(() => {
        refetch();
        setOpen(false);
    });

    const onPauseAction = () => {
        void submitCreateHold({
            hold_details: {
                hold_category: selectedOption ?? '',
                hold_is_practice_managed_pause: true,
            },
            orderId: order.id,
            show_hold_status_to_practice: true,
        });
    };

    return (
        <RootActionDialog
            showCloseButton
            disableActionsBorder
            loading={submittingCreateHold}
            open={open}
            setOpen={setOpen}
            onClose={closePauseOrderModal}
            maxWidth={'md'}
            title={'Pause this order'}
            content={
                <PauseOrderActionContent
                    order={order}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    orderHasSplitOrders={orderHasSplitOrders}
                    onCloseAction={closePauseOrderModal}
                    onPauseAction={onPauseAction}
                />
            }
            actions={orderHasSplitOrders && <SplitOrderBanner />}
            CustomButton={() => (
                <Button
                    data-test={'open-pause-order-modal-button'}
                    leftAlign={leftAlign}
                    variant={buttonVariant}
                    startIcon={'PauseIcon'}
                    onClick={openPauseOrderModal}
                >
                    Pause order
                </Button>
            )}
        />
    );
};
